import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import type { RenderSource, RenderRunner } from "./render-runner-type.ts";
import type { RenderWorkerIncomingData } from "./render-worker-data.ts";

// Interesting observation, main thread rendering was faster in one test i did. 2.5ms
// compared to 7-14. Pretty small example though. I'm keeping in worker for better ui
// responsiveness. Test again when have a larger project

function createRenderWorkerBridge(): RenderRunner<HTMLCanvasElement> {
	// Note: neither format of worker works locally for me atm. Either in a built preview or
	// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
	const renderWorker = new Worker(
		new URL("./render-worker.ts", import.meta.url),
		{
			type: "module",
		},
	);

	return {
		setSourceImage(sourceImage: Bitmap, utilityCanvas: HTMLCanvasElement) {
			const offscreenCanvas = utilityCanvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-source-image",
					utilityCanvas: offscreenCanvas,
					sourceImage,
				} satisfies RenderWorkerIncomingData,
				[offscreenCanvas],
			);
		},
		setPictureConfiguration(picture: PictureConfiguration) {
			renderWorker.postMessage(
				{
					type: "set-picture",
					picture,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setCanvas(canvas: HTMLCanvasElement) {
			const offscreenCanvas = canvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-canvas",
					canvas: offscreenCanvas,
				} satisfies RenderWorkerIncomingData,
				[offscreenCanvas],
			);
		},
		setSource(newSource: RenderSource | undefined) {
			renderWorker.postMessage(
				{
					type: "set-source",
					source: newSource,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			renderWorker.postMessage(
				{
					type: "set-draw-baseplates",
					drawBaseplates: newDrawBaseplates,
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement,
			canvasSize: GenericSize<number>,
		) {
			const utilityCanvas = newUtilityCanvas.transferControlToOffscreen();
			renderWorker.postMessage(
				{
					type: "set-utility-canvas",
					utilityCanvas,
					canvasSize,
				} satisfies RenderWorkerIncomingData,
				[utilityCanvas],
			);
		},
		clearUtilityCanvas() {
			renderWorker.postMessage(
				{
					type: "clear-utility-canvas",
				} satisfies RenderWorkerIncomingData,
				[],
			);
		},
		terminate() {
			renderWorker.terminate();
		},
	};
}

export { createRenderWorkerBridge };
